import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import Swal from "sweetalert2";

const WorkoutPage = () => {
  const [workouts, setWorkouts] = useState([]);
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDuration, setEditDuration] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("pending"); // 'all', 'pending', 'completed'

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  const fetchWorkouts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/workouts/getMyWorkouts`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();
      if (Array.isArray(data.workouts)) {
        // Check if data.workouts is an array
        setWorkouts(data.workouts); // Set workouts state with data.workouts
      } else {
        setWorkouts([]); // Set workouts state to an empty array if data.workouts is not an array
      }
    } catch (error) {
      console.error("Error:", error);
      setWorkouts([]);
    }
  };

  const handleAddWorkout = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/workouts/addWorkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ name, duration }),
        }
      )
        .then((res) => res.json())
        .then((data) => {
          if (!data.error) {
            Swal.fire({
              title: "Success",
              icon: "success",
              text: "Workout added to list",
            });
            fetchWorkouts();
            setName("");
            setDuration("");
            handleCloseModal();
          } else {
            Swal.fire({
              title: "error",
              icon: "error",
              text: data.error,
            });
          }
        });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDeleteWorkout = async (workoutId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/workouts/deleteWorkout/${workoutId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        Swal.fire({
          title: "Success",
          icon: "success",
          text: "Workout deleted successfully",
        });
        fetchWorkouts();
      } else {
        Swal.fire({
          title: "Error",
          icon: "error",
          text: "Failed to delete workout",
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleEditWorkout = (workout) => {
    setEditId(workout._id);
    setEditName(workout.name);
    setEditDuration(workout.duration);
  };

  const handleCompletedWorkout = async (workoutId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/workouts/completeWorkoutStatus/${workoutId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        Swal.fire({
          title: "Success",
          icon: "success",
          text: "Workout completed successfully",
        });
        fetchWorkouts();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleUpdateWorkout = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/workouts/updateWorkout/${editId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ name: editName, duration: editDuration }),
        }
      );

      if (response.ok) {
        Swal.fire({
          title: "Success",
          icon: "success",
          text: "Workout updated successfully",
        });
        fetchWorkouts();
        setEditId(null);
        setEditName("");
        setEditDuration("");
      } else {
        Swal.fire({
          title: "Error",
          icon: "error",
          text: "Failed to update workout",
        });
        console.error("Failed to update workout");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    fetchWorkouts();
  }, []);

  // Filtered workouts based on selected filter
  const filteredWorkouts =
    filter === "all"
      ? workouts
      : workouts.filter((workout) => workout.status === filter);

  return (
    <div>
      <h2>Workout List</h2>
      <div className="d-flex justify-content-end">
        <Button variant="primary" onClick={handleShowModal}>
          Add Workout
        </Button>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Workout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formWorkoutName">
              <Form.Label>Workout Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Workout Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="formWorkoutDuration">
              <Form.Label>Duration (ex: 30 mins)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleAddWorkout}>
            Add Workout
          </Button>
        </Modal.Footer>
      </Modal>

      <div>
        <Button
          variant={filter === "all" ? "primary" : "outline-primary"}
          className="mr-2"
          onClick={() => setFilter("all")}
        >
          All
        </Button>
        <Button
          variant={filter === "pending" ? "primary" : "outline-primary"}
          className="mr-2"
          onClick={() => setFilter("pending")}
        >
          Pending
        </Button>
        <Button
          variant={filter === "completed" ? "primary" : "outline-primary"}
          onClick={() => setFilter("completed")}
        >
          Completed
        </Button>
      </div>

      <div className="row">
        <div className="col-12">
          {filteredWorkouts.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped table-bordered table-hover table-sm text-center">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>DURATION</th>
                    <th>STATUS</th>
                    <th>DATE CREATED</th>
                    <th colSpan={3}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWorkouts.map((workout) => (
                    <tr key={workout._id}>
                      <td>
                        {editId === workout._id ? (
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="form-control form-control-sm"
                          />
                        ) : (
                          workout.name
                        )}
                      </td>
                      <td>
                        {editId === workout._id ? (
                          <input
                            type="text"
                            value={editDuration}
                            onChange={(e) => setEditDuration(e.target.value)}
                            className="form-control form-control-sm"
                          />
                        ) : (
                          workout.duration
                        )}
                      </td>
                      <td>{workout.status}</td>
                      <td>{formatDate(workout.dateAdded)}</td>
                      <td>
                        {workout.status === "pending" &&
                          (editId === workout._id ? (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={handleUpdateWorkout}
                            >
                              Save
                            </Button>
                          ) : (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEditWorkout(workout)}
                            >
                              Edit
                            </Button>
                          ))}
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteWorkout(workout._id)}
                        >
                          Delete
                        </Button>
                      </td>
                      <td>
                        {workout.status === "pending" && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleCompletedWorkout(workout._id)}
                          >
                            Mark as Completed
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4">No workouts available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkoutPage;
